import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { apiClient } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { toast } from "sonner";
import { Eye, EyeOff, Lock, CheckCircle2, AlertCircle } from "lucide-react";

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!token) setError("No reset token provided. Please use the link from your email.");
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    setLoading(true);
    try {
      await apiClient.post("/auth/reset-password", { token, new_password: password });
      setSuccess(true);
    } catch (err) {
      const detail = err.response?.data?.detail || "Failed to reset password";
      setError(detail);
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-8 bg-slate-50" data-testid="reset-password-page">
      <Card className="w-full max-w-md shadow-floating border-0">
        <CardHeader className="space-y-1 text-center pb-2">
          <CardTitle className="font-heading text-2xl font-bold text-sag-navy">
            {success ? "Password Reset" : error && !token ? "Invalid Link" : "Set New Password"}
          </CardTitle>
          <CardDescription className="text-slate-500">
            {success
              ? "Your password has been updated."
              : error && !token
              ? "The reset link is invalid or missing."
              : "Enter your new password below."}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {success ? (
            <div className="text-center space-y-4" data-testid="password-reset-success">
              <CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto" />
              <p className="text-sm text-slate-600">You can now login with your new password.</p>
              <Link to="/login">
                <Button className="bg-sag-navy hover:bg-sag-navy-800 w-full" data-testid="go-to-login">
                  <Lock className="h-4 w-4 mr-2" /> Go to Login
                </Button>
              </Link>
            </div>
          ) : error && !token ? (
            <div className="text-center space-y-4" data-testid="invalid-token-error">
              <AlertCircle className="h-12 w-12 text-red-500 mx-auto" />
              <p className="text-sm text-red-600">{error}</p>
              <Link to="/forgot-password">
                <Button variant="outline" data-testid="request-new-link">Request New Reset Link</Button>
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="password" className="text-slate-700">New Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Minimum 8 characters"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={8}
                    className="h-11 pr-10 border-slate-200 focus:border-sag-navy focus:ring-sag-navy/20"
                    data-testid="new-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm" className="text-slate-700">Confirm Password</Label>
                <Input
                  id="confirm"
                  type={showPassword ? "text" : "password"}
                  placeholder="Repeat your password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  className="h-11 border-slate-200 focus:border-sag-navy focus:ring-sag-navy/20"
                  data-testid="confirm-password-input"
                />
              </div>
              {error && (
                <p className="text-sm text-red-600" data-testid="reset-error">{error}</p>
              )}
              <Button
                type="submit"
                disabled={loading}
                className="w-full h-11 bg-sag-navy hover:bg-sag-navy-800 text-white font-semibold"
                data-testid="reset-password-button"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <span className="animate-spin h-4 w-4 border-2 border-white/30 border-t-white rounded-full" />
                    Resetting...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Lock className="h-4 w-4" />
                    Reset Password
                  </span>
                )}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
